"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GOAL_TREE_SUBGOAL_REF_TYPE = exports.GoalTreePatchValidationError = exports.GoalTreePatchSchema = exports.GoalTreePatchCommandSchema = exports.GoalTreeNewStepSchema = exports.GoalTreeEnvelopeSchema = exports.GoalTreeUiHintsSchema = exports.GoalTreeVerificationSchema = exports.GoalTreeNextStepSchema = exports.GoalTreeGapSchema = exports.GoalTreeProgressSchema = exports.GoalTreeNodeSchema = exports.GoalTreeActionSchema = exports.GoalTreeStepEvidenceSchema = exports.GoalTreeRefSchema = exports.GoalTreeJsonValueSchema = exports.GoalStepModelPolicySchema = exports.GoalTreeInteractionModeSchema = exports.GoalTreeReasoningLevelSchema = exports.GoalTreeModelTierSchema = exports.GoalTreeGapSeveritySchema = exports.GoalTreeNodeStatusSchema = exports.CHAT_GOAL_STEP_KIND = exports.CHAT_GOAL_ROOT_KIND = exports.GoalTreeDomainSchema = exports.GOAL_TREE_SCHEMA_VERSION = void 0;
exports.toGoalTreeJsonValue = toGoalTreeJsonValue;
exports.toOptionalGoalTreeJsonValue = toOptionalGoalTreeJsonValue;
exports.assertGoalTreePatchAppliesToTree = assertGoalTreePatchAppliesToTree;
exports.findGoalTreeNode = findGoalTreeNode;
exports.isGoalTreeModelPolicyEditableNode = isGoalTreeModelPolicyEditableNode;
exports.goalTreeSubgoalIds = goalTreeSubgoalIds;
exports.collectGoalTreeSubgoalIds = collectGoalTreeSubgoalIds;
exports.countGoalTreeProgress = countGoalTreeProgress;
exports.countChatGoalProgress = countChatGoalProgress;
exports.isChatGoalPlanCommand = isChatGoalPlanCommand;
exports.applyChatGoalTreePatch = applyChatGoalTreePatch;
const zod_1 = require("zod");
exports.GOAL_TREE_SCHEMA_VERSION = 1;
exports.GoalTreeDomainSchema = zod_1.z.enum(['app_base', 'automation', 'chat']);
/** The root of a chat goal: its label is the objective. */
exports.CHAT_GOAL_ROOT_KIND = 'chat_goal';
/** A step of a chat goal, done when its `doneWhen` holds and evidence shows it. */
exports.CHAT_GOAL_STEP_KIND = 'chat_step';
exports.GoalTreeNodeStatusSchema = zod_1.z.enum([
    'ready',
    'configured',
    'defaulted',
    'missing',
    'invalid',
    'blocked',
    'running',
    'completed',
]);
exports.GoalTreeGapSeveritySchema = zod_1.z.enum([
    'info',
    'warning',
    'blocking',
]);
exports.GoalTreeModelTierSchema = zod_1.z.enum(['economy', 'standard', 'pro']);
exports.GoalTreeReasoningLevelSchema = zod_1.z.enum([
    'off',
    'low',
    'medium',
    'high',
]);
exports.GoalTreeInteractionModeSchema = zod_1.z.enum(['chat', 'agent']);
exports.GoalStepModelPolicySchema = zod_1.z
    .object({
    defaultTier: exports.GoalTreeModelTierSchema,
    allowedTiers: zod_1.z.array(exports.GoalTreeModelTierSchema).min(1),
    reasoning: exports.GoalTreeReasoningLevelSchema,
    reason: zod_1.z.string().min(1),
    userOverride: zod_1.z
        .object({
        tier: exports.GoalTreeModelTierSchema.optional(),
        reasoning: exports.GoalTreeReasoningLevelSchema.optional(),
        updatedAt: zod_1.z.string().min(1).optional(),
    })
        .strict()
        .optional(),
    costEstimate: zod_1.z
        .object({
        credits: zod_1.z.number().nonnegative().optional(),
        label: zod_1.z.string().min(1).optional(),
    })
        .strict()
        .optional(),
})
    .strict()
    .superRefine((policy, ctx) => {
    if (!policy.allowedTiers.includes(policy.defaultTier)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'defaultTier must be present in allowedTiers',
            path: ['defaultTier'],
        });
    }
});
exports.GoalTreeJsonValueSchema = zod_1.z.lazy(() => zod_1.z.union([
    zod_1.z.string(),
    zod_1.z.number(),
    zod_1.z.boolean(),
    zod_1.z.null(),
    zod_1.z.array(exports.GoalTreeJsonValueSchema),
    zod_1.z.record(zod_1.z.string(), exports.GoalTreeJsonValueSchema),
]));
function toGoalTreeJsonValue(value) {
    if (value === null ||
        typeof value === 'string' ||
        typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (Array.isArray(value)) {
        return value
            .filter(item => item !== undefined)
            .map(item => toGoalTreeJsonValue(item));
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value)
            .filter(([, item]) => item !== undefined)
            .map(([key, item]) => [key, toGoalTreeJsonValue(item)]));
    }
    return null;
}
function toOptionalGoalTreeJsonValue(value) {
    return value === undefined ? undefined : toGoalTreeJsonValue(value);
}
exports.GoalTreeRefSchema = zod_1.z
    .object({
    id: zod_1.z.string().min(1),
    type: zod_1.z.string().min(1),
    label: zod_1.z.string().min(1).optional(),
})
    .strict();
/** What shows that a step is done: a short statement and where it can be checked. */
exports.GoalTreeStepEvidenceSchema = zod_1.z
    .object({
    summary: zod_1.z.string().min(1),
    refs: zod_1.z.array(exports.GoalTreeRefSchema).optional(),
    recordedAt: zod_1.z.string().min(1),
})
    .strict();
exports.GoalTreeActionSchema = zod_1.z
    .object({
    actionId: zod_1.z.string().min(1),
    kind: zod_1.z.string().min(1),
    label: zod_1.z.string().min(1),
    disabledReason: zod_1.z.string().min(1).optional(),
    requiresConfirmation: zod_1.z.boolean().optional(),
})
    .strict();
exports.GoalTreeNodeSchema = zod_1.z.lazy(() => zod_1.z
    .object({
    nodeId: zod_1.z.string().min(1),
    kind: zod_1.z.string().min(1),
    label: zod_1.z.string().min(1),
    status: exports.GoalTreeNodeStatusSchema,
    required: zod_1.z.boolean(),
    valueSummary: zod_1.z.string().min(1).optional(),
    detail: exports.GoalTreeJsonValueSchema.optional(),
    refs: zod_1.z.array(exports.GoalTreeRefSchema).optional(),
    actions: zod_1.z.array(exports.GoalTreeActionSchema).optional(),
    modelPolicy: exports.GoalStepModelPolicySchema.optional(),
    doneWhen: zod_1.z.string().min(1).optional(),
    evidence: exports.GoalTreeStepEvidenceSchema.optional(),
    children: zod_1.z.array(exports.GoalTreeNodeSchema),
})
    .strict());
exports.GoalTreeProgressSchema = zod_1.z
    .object({
    ready: zod_1.z.number().int().nonnegative(),
    configured: zod_1.z.number().int().nonnegative(),
    defaulted: zod_1.z.number().int().nonnegative(),
    missing: zod_1.z.number().int().nonnegative(),
    invalid: zod_1.z.number().int().nonnegative(),
    blocked: zod_1.z.number().int().nonnegative(),
    completed: zod_1.z.number().int().nonnegative().optional(),
    total: zod_1.z.number().int().nonnegative(),
})
    .strict();
exports.GoalTreeGapSchema = zod_1.z
    .object({
    gapId: zod_1.z.string().min(1),
    nodeId: zod_1.z.string().min(1),
    severity: exports.GoalTreeGapSeveritySchema,
    blocking: zod_1.z.boolean(),
    reason: zod_1.z.string().min(1),
    allowedActions: zod_1.z.array(zod_1.z.string().min(1)),
    detail: exports.GoalTreeJsonValueSchema.optional(),
})
    .strict();
exports.GoalTreeNextStepSchema = zod_1.z
    .object({
    nextStepId: zod_1.z.string().min(1),
    nodeId: zod_1.z.string().min(1),
    actionKind: zod_1.z.string().min(1),
    label: zod_1.z.string().min(1),
    requiredConfirmation: zod_1.z.string().min(1).optional(),
    modelPolicy: exports.GoalStepModelPolicySchema.optional(),
})
    .strict();
exports.GoalTreeVerificationSchema = zod_1.z
    .object({
    status: zod_1.z.enum(['not_started', 'running', 'passed', 'failed', 'skipped']),
    checks: zod_1.z.array(zod_1.z
        .object({
        checkId: zod_1.z.string().min(1),
        label: zod_1.z.string().min(1),
        status: zod_1.z.enum([
            'not_started',
            'running',
            'passed',
            'failed',
            'skipped',
        ]),
        reason: zod_1.z.string().min(1).optional(),
    })
        .strict()),
})
    .strict();
exports.GoalTreeUiHintsSchema = zod_1.z
    .object({
    defaultExpandedDepth: zod_1.z.number().int().nonnegative().optional(),
    renderer: zod_1.z.string().min(1).optional(),
    orientation: zod_1.z.enum(['vertical', 'horizontal']).default('vertical'),
    allowUserPatches: zod_1.z.boolean().default(true),
})
    .strict();
exports.GoalTreeEnvelopeSchema = zod_1.z
    .object({
    schemaVersion: zod_1.z.literal(exports.GOAL_TREE_SCHEMA_VERSION),
    domain: exports.GoalTreeDomainSchema,
    interactionMode: exports.GoalTreeInteractionModeSchema.default('agent'),
    mode: zod_1.z.string().min(1),
    target: zod_1.z.record(zod_1.z.string(), exports.GoalTreeJsonValueSchema),
    root: exports.GoalTreeNodeSchema,
    progress: exports.GoalTreeProgressSchema,
    gaps: zod_1.z.array(exports.GoalTreeGapSchema),
    nextSteps: zod_1.z.array(exports.GoalTreeNextStepSchema),
    diff: exports.GoalTreeJsonValueSchema.optional(),
    recipe: exports.GoalTreeJsonValueSchema.optional(),
    verification: exports.GoalTreeVerificationSchema.optional(),
    uiHints: exports.GoalTreeUiHintsSchema.prefault({
        allowUserPatches: true,
        orientation: 'vertical',
    }),
    payload: zod_1.z.record(zod_1.z.string(), exports.GoalTreeJsonValueSchema).optional(),
})
    .strict();
/** A step the user or the planner adds; its id is chosen by the caller so a preview and the stored plan agree. */
exports.GoalTreeNewStepSchema = zod_1.z
    .object({
    stepId: zod_1.z.string().min(1),
    label: zod_1.z.string().min(1),
    doneWhen: zod_1.z.string().min(1),
    modelPolicy: exports.GoalStepModelPolicySchema.optional(),
})
    .strict();
exports.GoalTreePatchCommandSchema = zod_1.z.discriminatedUnion('type', [
    zod_1.z
        .object({
        type: zod_1.z.literal('setNodeValue'),
        nodeId: zod_1.z.string().min(1),
        value: exports.GoalTreeJsonValueSchema,
    })
        .strict(),
    zod_1.z
        .object({
        type: zod_1.z.literal('acceptDefault'),
        nodeId: zod_1.z.string().min(1),
    })
        .strict(),
    zod_1.z
        .object({
        type: zod_1.z.literal('bindEntity'),
        nodeId: zod_1.z.string().min(1),
        entity: exports.GoalTreeRefSchema,
    })
        .strict(),
    zod_1.z
        .object({
        type: zod_1.z.literal('setStepModelPolicy'),
        nodeId: zod_1.z.string().min(1),
        policy: exports.GoalStepModelPolicySchema,
    })
        .strict(),
    zod_1.z
        .object({
        type: zod_1.z.literal('approveConfirmation'),
        nodeId: zod_1.z.string().min(1),
        confirmationKey: zod_1.z.string().min(1),
    })
        .strict(),
    // Plan commands of a chat goal; `nodeId` of addStep is the parent.
    zod_1.z
        .object({
        type: zod_1.z.literal('addStep'),
        nodeId: zod_1.z.string().min(1),
        afterNodeId: zod_1.z.string().min(1).nullable(),
        step: exports.GoalTreeNewStepSchema,
    })
        .strict(),
    zod_1.z
        .object({ type: zod_1.z.literal('removeStep'), nodeId: zod_1.z.string().min(1) })
        .strict(),
    zod_1.z
        .object({
        type: zod_1.z.literal('moveStep'),
        nodeId: zod_1.z.string().min(1),
        toParentNodeId: zod_1.z.string().min(1),
        afterNodeId: zod_1.z.string().min(1).nullable(),
    })
        .strict(),
    zod_1.z
        .object({
        type: zod_1.z.literal('editStep'),
        nodeId: zod_1.z.string().min(1),
        label: zod_1.z.string().min(1).optional(),
        doneWhen: zod_1.z.string().min(1).optional(),
    })
        .strict(),
    zod_1.z
        .object({
        type: zod_1.z.literal('setObjective'),
        nodeId: zod_1.z.string().min(1),
        objective: zod_1.z.string().min(1),
    })
        .strict(),
]);
exports.GoalTreePatchSchema = zod_1.z
    .object({
    goalId: zod_1.z.string().min(1).optional(),
    domain: exports.GoalTreeDomainSchema,
    commands: zod_1.z.array(exports.GoalTreePatchCommandSchema).min(1),
})
    .strict();
class GoalTreePatchValidationError extends Error {
    constructor(message, code, nodeId) {
        super(message);
        this.code = code;
        this.nodeId = nodeId;
        this.name = 'GoalTreePatchValidationError';
    }
}
exports.GoalTreePatchValidationError = GoalTreePatchValidationError;
function assertGoalTreePatchAppliesToTree(tree, patch) {
    const parsedTree = exports.GoalTreeEnvelopeSchema.parse(tree);
    const parsedPatch = exports.GoalTreePatchSchema.parse(patch);
    if (parsedTree.domain === 'chat') {
        // A plan patch may refer to a step it adds itself: check it by applying it in order.
        applyChatGoalTreePatch(parsedTree, parsedPatch);
        return;
    }
    if (parsedPatch.domain !== parsedTree.domain) {
        throw new GoalTreePatchValidationError(`GoalTree patch domain ${parsedPatch.domain} does not match tree domain ${parsedTree.domain}`, 'goal_tree_patch_domain_mismatch');
    }
    for (const command of parsedPatch.commands) {
        const node = findGoalTreeNode(parsedTree.root, command.nodeId);
        if (!node) {
            throw new GoalTreePatchValidationError(`GoalTree patch references unknown node ${command.nodeId}`, 'goal_tree_patch_unknown_node', command.nodeId);
        }
        if (!isPatchCommandAllowed(parsedTree, node, command)) {
            throw new GoalTreePatchValidationError(`GoalTree patch command ${command.type} is not allowed for node ${command.nodeId}`, 'goal_tree_patch_action_not_allowed', command.nodeId);
        }
    }
}
function findGoalTreeNode(node, nodeId) {
    if (node.nodeId === nodeId)
        return node;
    for (const child of node.children) {
        const found = findGoalTreeNode(child, nodeId);
        if (found)
            return found;
    }
    return null;
}
function isGoalTreeModelPolicyEditableNode(tree, node) {
    // A chat step runs with the model its plan proposed until the user picks another; done and running steps keep theirs.
    if (tree.domain === 'chat') {
        return (isChangeableStep(node) &&
            node.children.length === 0 &&
            node.modelPolicy !== undefined);
    }
    if (isStructuralGoalTreeNodeKind(node.kind))
        return false;
    if (node.children.length > 0)
        return false;
    return getGoalTreeNodeExecutableActionKinds(tree, node).size > 0;
}
function isStructuralGoalTreeNodeKind(kind) {
    return (kind === 'automation_goal' ||
        kind === exports.CHAT_GOAL_ROOT_KIND ||
        kind === 'automation_phase' ||
        kind === 'missing_runbook' ||
        kind === 'app_base_goal');
}
function isPatchCommandAllowed(tree, node, command) {
    if (command.type === 'setStepModelPolicy') {
        return isGoalTreeModelPolicyEditableNode(tree, node);
    }
    if (isChatGoalPlanCommand(command))
        return false;
    const allowedActions = getGoalTreeNodeExecutableActionKinds(tree, node);
    const detail = isGoalTreeRecord(node.detail) ? node.detail : {};
    if (command.type === 'approveConfirmation') {
        return (node.status === 'blocked' ||
            allowedActions.has('approveConfirmation') ||
            allowedActions.has('approve_confirmation'));
    }
    if (command.type === 'acceptDefault') {
        return (node.status === 'defaulted' ||
            Object.prototype.hasOwnProperty.call(detail, 'defaultValue') ||
            allowedActions.has('acceptDefault') ||
            allowedActions.has('accept_default'));
    }
    if (command.type === 'bindEntity') {
        return (allowedActions.has('bindEntity') ||
            allowedActions.has('bind_workspace_entity') ||
            allowedActions.has('provide_input'));
    }
    if (command.type === 'setNodeValue') {
        return (node.status === 'missing' ||
            node.status === 'invalid' ||
            node.status === 'defaulted' ||
            allowedActions.has('setNodeValue') ||
            allowedActions.has('set_intent') ||
            allowedActions.has('choose_typed_value') ||
            allowedActions.has('provide_override_reason') ||
            allowedActions.has('provide_input') ||
            allowedActions.has('fix_step'));
    }
    return false;
}
function getGoalTreeNodeExecutableActionKinds(tree, node) {
    return new Set([
        ...(node.actions ?? []).map(action => action.kind),
        ...tree.nextSteps
            .filter(step => step.nodeId === node.nodeId)
            .map(step => step.actionKind),
        ...tree.gaps
            .filter(gap => gap.nodeId === node.nodeId)
            .flatMap(gap => gap.allowedActions),
    ]);
}
function isGoalTreeRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/** Counts the nodes of a tree by status; every domain reports progress this way. */
/** The ref type of a goal a step started; the step is done when that goal is. */
exports.GOAL_TREE_SUBGOAL_REF_TYPE = 'agent_goal';
/** The goals a node started, in the order it started them. */
function goalTreeSubgoalIds(node) {
    return (node.refs ?? [])
        .filter(ref => ref.type === exports.GOAL_TREE_SUBGOAL_REF_TYPE)
        .map(ref => ref.id);
}
/** The goals the nodes of a tree started. */
function collectGoalTreeSubgoalIds(node) {
    return [
        ...goalTreeSubgoalIds(node),
        ...node.children.flatMap(collectGoalTreeSubgoalIds),
    ];
}
function countGoalTreeProgress(root) {
    return countNodes([root]);
}
/** A chat goal counts its steps; the root is the objective, not a step. */
function countChatGoalProgress(root) {
    return countNodes(root.children);
}
function countNodes(nodes) {
    const progress = {
        ready: 0,
        configured: 0,
        defaulted: 0,
        missing: 0,
        invalid: 0,
        blocked: 0,
        completed: 0,
        total: 0,
    };
    const visit = (node) => {
        progress.total += 1;
        if (node.status in progress) {
            progress[node.status] += 1;
        }
        node.children.forEach(visit);
    };
    nodes.forEach(visit);
    return progress;
}
const CHAT_PLAN_COMMANDS = new Set([
    'addStep',
    'removeStep',
    'moveStep',
    'editStep',
    'setObjective',
]);
/** Commands that change the plan of a chat goal; other domains refuse them. */
function isChatGoalPlanCommand(command) {
    return CHAT_PLAN_COMMANDS.has(command.type);
}
/**
 * Applies the plan commands of a chat goal, and the user's model choice for a
 * step, in order and returns the new tree;
 * the input stays untouched. Each command is checked against the tree as the
 * commands before it left it, so one patch can add a step and then move it.
 * The server stores the result and the panel shows it as the preview of a
 * proposed change. Done steps and running steps keep their place.
 */
function applyChatGoalTreePatch(tree, patch) {
    const parsedTree = exports.GoalTreeEnvelopeSchema.parse(tree);
    const parsedPatch = exports.GoalTreePatchSchema.parse(patch);
    if (parsedTree.domain !== 'chat' || parsedPatch.domain !== 'chat') {
        throw new GoalTreePatchValidationError(`GoalTree patch domain ${parsedPatch.domain} does not match tree domain ${parsedTree.domain}`, 'goal_tree_patch_domain_mismatch');
    }
    if (!parsedTree.uiHints.allowUserPatches) {
        throw new GoalTreePatchValidationError('This goal does not take plan changes', 'goal_tree_patch_action_not_allowed');
    }
    let root = cloneNode(parsedTree.root);
    let target = { ...parsedTree.target };
    for (const command of parsedPatch.commands) {
        if (!isChatGoalPlanCommand(command) &&
            command.type !== 'setStepModelPolicy') {
            throw new GoalTreePatchValidationError(`GoalTree patch command ${command.type} is not a plan command`, 'goal_tree_patch_action_not_allowed', command.nodeId);
        }
        const node = requireNode(root, command.nodeId);
        if (command.type === 'setObjective') {
            if (node.kind !== exports.CHAT_GOAL_ROOT_KIND)
                notAllowed(command);
            node.label = command.objective;
            target = { ...target, objective: command.objective };
        }
        else if (command.type === 'addStep') {
            if (!isOpenContainer(node))
                notAllowed(command);
            if (findGoalTreeNode(root, command.step.stepId)) {
                throw new GoalTreePatchValidationError(`GoalTree already has node ${command.step.stepId}`, 'goal_tree_patch_duplicate_node', command.step.stepId);
            }
            insertAfter(node, command.afterNodeId, {
                nodeId: command.step.stepId,
                kind: exports.CHAT_GOAL_STEP_KIND,
                label: command.step.label,
                status: 'ready',
                required: true,
                doneWhen: command.step.doneWhen,
                ...(command.step.modelPolicy
                    ? { modelPolicy: command.step.modelPolicy }
                    : {}),
                children: [],
            });
        }
        else if (command.type === 'setStepModelPolicy') {
            // The user picks a model and depth for the step; what the plan proposed and why stays.
            const override = command.policy.userOverride;
            if (!node.modelPolicy ||
                !isGoalTreeModelPolicyEditableNode(parsedTree, node) ||
                !override ||
                (override.tier !== undefined &&
                    !node.modelPolicy.allowedTiers.includes(override.tier))) {
                notAllowed(command);
            }
            node.modelPolicy = { ...node.modelPolicy, userOverride: override };
        }
        else if (command.type === 'editStep') {
            if (!isChangeableStep(node))
                notAllowed(command);
            if (!command.label && !command.doneWhen)
                notAllowed(command);
            if (command.label)
                node.label = command.label;
            if (command.doneWhen)
                node.doneWhen = command.doneWhen;
        }
        else if (command.type === 'removeStep') {
            if (!isChangeableStep(node) || hasDoneOrRunning(node)) {
                notAllowed(command);
            }
            detach(root, node.nodeId);
        }
        else {
            if (!isChangeableStep(node) || hasDoneOrRunning(node)) {
                notAllowed(command);
            }
            const parent = requireNode(root, command.toParentNodeId);
            if (!isOpenContainer(parent) ||
                findGoalTreeNode(node, parent.nodeId) ||
                command.afterNodeId === node.nodeId) {
                throw new GoalTreePatchValidationError(`GoalTree step ${node.nodeId} cannot move under ${parent.nodeId}`, 'goal_tree_patch_invalid_position', node.nodeId);
            }
            detach(root, node.nodeId);
            insertAfter(parent, command.afterNodeId, node);
        }
        root = exports.GoalTreeNodeSchema.parse(root);
    }
    const kept = (nodeId) => findGoalTreeNode(root, nodeId) !== null;
    return exports.GoalTreeEnvelopeSchema.parse({
        ...parsedTree,
        target,
        root,
        progress: countChatGoalProgress(root),
        gaps: parsedTree.gaps.filter(gap => kept(gap.nodeId)),
        nextSteps: parsedTree.nextSteps.filter(step => kept(step.nodeId)),
    });
}
function cloneNode(node) {
    return { ...node, children: node.children.map(cloneNode) };
}
function requireNode(root, nodeId) {
    const node = findGoalTreeNode(root, nodeId);
    if (!node) {
        throw new GoalTreePatchValidationError(`GoalTree patch references unknown node ${nodeId}`, 'goal_tree_patch_unknown_node', nodeId);
    }
    return node;
}
function notAllowed(command) {
    throw new GoalTreePatchValidationError(`GoalTree patch command ${command.type} is not allowed for node ${command.nodeId}`, 'goal_tree_patch_action_not_allowed', command.nodeId);
}
/** The goal itself or a step that is not done yet can take steps. */
function isOpenContainer(node) {
    if (node.kind === exports.CHAT_GOAL_ROOT_KIND)
        return true;
    return node.kind === exports.CHAT_GOAL_STEP_KIND && node.status !== 'completed';
}
function isChangeableStep(node) {
    return (node.kind === exports.CHAT_GOAL_STEP_KIND &&
        node.status !== 'completed' &&
        node.status !== 'running');
}
function hasDoneOrRunning(node) {
    return node.children.some(child => child.status === 'completed' ||
        child.status === 'running' ||
        hasDoneOrRunning(child));
}
function insertAfter(parent, afterNodeId, node) {
    if (afterNodeId === null) {
        parent.children.unshift(node);
        return;
    }
    const index = parent.children.findIndex(child => child.nodeId === afterNodeId);
    if (index < 0) {
        throw new GoalTreePatchValidationError(`GoalTree node ${afterNodeId} is no child of ${parent.nodeId}`, 'goal_tree_patch_invalid_position', afterNodeId);
    }
    parent.children.splice(index + 1, 0, node);
}
function detach(root, nodeId) {
    const index = root.children.findIndex(child => child.nodeId === nodeId);
    if (index >= 0) {
        root.children.splice(index, 1);
        return;
    }
    root.children.forEach(child => detach(child, nodeId));
}
